import React from 'react';
import { Tooltip } from 'antd';
import { RequestDataResponse } from 'core/dashboard/types';
import { VISIT_REQUEST_STATUS_ID, REQUEST_TYPE_VISITOR, REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING, RequestType } from 'core/utils/value';
import { isNull } from 'underscore';
import { formatTime as formatTime24 } from 'core/utils/date';
import { AuthUserDataResponse } from '../../core/user/types';

// Statuses where the request is still out for approval (vs already approved).
const AWAITING_APPROVAL_STATUS_IDS: string[] = [
    VISIT_REQUEST_STATUS_ID.PENDING_COST_APPROVAL,
    VISIT_REQUEST_STATUS_ID.PENDING_APPROVAL,
];

const renderRefNumber = (data: number) => {
    if (data) {
        return String(data).padStart(3, '0');
    }
    return '';
};

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
    [REQUEST_TYPE_VISITOR]: 'Visitor Request',
    [REQUEST_TYPE_ACCESS]: 'Access Request',
    [REQUEST_TYPE_GUARDING]: 'Guarding Request',
};

// Fallback to 'Unknown' guards against an out-of-contract value arriving from
// the backend despite the RequestType union on the client.
const renderRequestType = (_:undefined, data:RequestDataResponse) => REQUEST_TYPE_LABELS[data.type] || 'Unknown';

// Retail sites show their store code in brackets, matching the request view.
const renderSite = (_:undefined, data:RequestDataResponse) => {
    const name = data.site?.name;
    const storeCode = data.site?.storeCode;
    return name && storeCode ? `${name} (${storeCode})` : name;
};

const renderRequester = (_:undefined, data:RequestDataResponse) => {
    // Guest bookings have no requester account — fall back to the invited name.
    const name = data.guestRequesterName || data.requester?.name;
    const onBehalf = data.onBehalfEmail;
    // One-off booking links: who generated the link for this guest.
    const generatedBy = data.generatedBy?.name;
    if (onBehalf || generatedBy) {
        return (
            <div>
                <div>{name}</div>
                {onBehalf && <div className="on-behalf-label">on behalf of {onBehalf}</div>}
                {generatedBy && <div className="on-behalf-label">generated by {generatedBy}</div>}
            </div>
        );
    }
    return name;
};

const renderApprover = (_:undefined, data:RequestDataResponse, userData:AuthUserDataResponse) => {
    const displayName = (data?.vodafoneMitieApprover?.email && userData.email === data.vodafoneMitieApprover.email
        ? data.vodafoneMitieApprover.name
        : null) || data.approver?.name || data?.vodafoneMitieApprover?.email || '-';

    // While still awaiting approval the request is pushed out to every approver
    // valid for its work types, so let the admin hover to see that full list.
    // Once approved we show only the single approver — hence we gate on the two
    // awaiting-approval status ids (not "Approved & Pending Schedule").
    const approvers = data.approvers ?? [];
    const isAwaitingApproval = AWAITING_APPROVAL_STATUS_IDS.includes(data.statusId ?? '');

    if (!isAwaitingApproval || approvers.length === 0) {
        return displayName;
    }

    const approverNames = approvers.map((approver) => approver.name).join(', ');

    return (
        <Tooltip
            title={(
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {approvers.map((approver) => (
                        <li key={approver.email}>{approver.name}</li>
                    ))}
                </ul>
            )}>
            <span
                style={{ textDecoration: 'underline dotted', cursor: 'help' }}
                aria-label={`Pushed to approvers: ${approverNames}`}>
                {displayName}
            </span>
        </Tooltip>
    );
};

const renderPeriod = (_: undefined, record: RequestDataResponse) => {
    const { earliest, latest } = record?.requestedPeriod || {};
    const earliestDate = new Date(earliest?.date);
    const latestDate = new Date(latest?.date);

    // Night/overnight shifts end on the following day: the end time falls
    // before the start time (e.g. a Tue night shift 19:00 -> 07:00 ends Wed
    // 07:00). Advance the end date so the dashboard shows the true end date,
    // not the shift's start date. (Same convention as the night-shift check
    // in the API: startTime > endTime => crosses midnight.)
    if (latest?.startTime && latest?.endTime && latest.startTime > latest.endTime) {
        latestDate.setDate(latestDate.getDate() + 1);
    }

    // Format date to dd/mm/yyyy
    const formatDate = (date: Date) => {
        if (!date) return '-';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        if (!day || !month || !year) return '';
        return `${day}/${month}/${year}`;
    };

    // Format time to 24-hour format (HH:mm)
    const formatTime = (time: string | undefined) => (time ? formatTime24(time) : '');
    const formattedEarliestDate = formatDate(earliestDate);
    const formattedLatestDate = formatDate(latestDate);
    const formattedEarliestTime = formatTime(earliest?.startTime);
    const formattedLatestTime = formatTime(latest?.endTime);

    // Check if the period is on the same day
    if (earliestDate.toDateString() === latestDate.toDateString()) {
        return (
            <div>
                <div>{formattedEarliestDate}</div>
                <div>{`${formattedEarliestTime} - ${formattedLatestTime}`}</div>
            </div>
        );
    } if (earliestDate.toDateString() !== latestDate.toDateString()) {
        return (
            <div>
                <div>{formattedEarliestDate}</div>
                <div>{`Start: ${formattedEarliestTime}`}</div>
                <div>{formattedLatestDate}</div>
                <div>{`End: ${formattedLatestTime}`}</div>
            </div>
        );
    }
    return '-';
};

const formatSubmittedDateTime = (raw: string | null | undefined): string => {
    if (isNull(raw) || !raw) return '-';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const renderDateSubmitted = (_:undefined, record:RequestDataResponse) => formatSubmittedDateTime(record?.dateSubmitted);

export { renderRefNumber, renderRequestType, renderSite, renderRequester, renderPeriod, renderDateSubmitted, renderApprover, formatSubmittedDateTime };
